import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Modal,
  Row,
} from "react-bootstrap";
import Moment from "react-moment";
import ReactToPrint from "react-to-print";
import {
  formatCurrency,
  formatDynamicDisplayValue,
  getTransactionFieldRows,
  isImagePath,
} from "./SCRV2InvoiceDetailHelpers";
import { SCR_V2_NESTED_CARD_STYLE } from "./SCRV2SummaryUiShared";
import SCRV2InvoiceDetailPrint from "./SCRV2InvoiceDetailPrint";

const isAdjustmentTransaction = (transaction) => {
  const category = (transaction?.transactionCategory || "").toLowerCase();
  return category.includes("adjustment");
};

const MODAL_BODY_SCROLL_STYLE = {
  maxHeight: "calc(100vh - 11rem)",
  overflowY: "auto",
  overflowX: "hidden",
};

const ModalPanel = ({ title, icon, children, className = "", headerExtra = null }) => (
  <div
    className={`card mb-0 h-100 scr-invoice-modal-panel ${className}`.trim()}
    style={SCR_V2_NESTED_CARD_STYLE}
  >
    <div className="card-header py-1 bg-white scr-invoice-modal-panel__header">
      <h6 className="card-title mb-0 d-flex align-items-center justify-content-between w-100">
        <span className="d-flex align-items-center min-w-0">
          {icon ? (
            <span
              className="d-inline-flex align-items-center justify-content-center rounded bg-primary text-white mr-2 flex-shrink-0"
              style={{ width: 22, height: 22, fontSize: "0.7rem" }}
            >
              <i className={`fas ${icon}`} />
            </span>
          ) : null}
          <b className="text-truncate">{title}</b>
        </span>
        {headerExtra ? (
          <span className="scr-invoice-modal-panel__header-extra flex-shrink-0 ml-2">{headerExtra}</span>
        ) : null}
      </h6>
    </div>
    <div className="card-body py-1 px-1">{children}</div>
  </div>
);

const getParticipationModeMeta = (mode, label = "") => {
  switch (Number(mode)) {
    case 1:
      return {
        shortLabel: "Recovery",
        fullLabel: label || "Recovery Only",
        icon: "fa-history",
        tone: "recovery",
      };
    case 2:
      return {
        shortLabel: "Base Only",
        fullLabel: label || "Invoice Base Only",
        icon: "fa-file-invoice",
        tone: "base",
      };
    case 3:
      return {
        shortLabel: "Base + Recovery",
        fullLabel: label || "Invoice Base + Recovery",
        icon: "fa-layer-group",
        tone: "combined",
      };
    default:
      return {
        shortLabel: label || "-",
        fullLabel: label || "-",
        icon: "fa-info-circle",
        tone: "default",
      };
  }
};

const ParticipationModeChip = ({ mode, label }) => {
  const numericMode = Number(mode);
  // Only Recovery participation is shown; hide Base Only and other modes.
  if (numericMode !== 1) {
    return null;
  }

  const meta = getParticipationModeMeta(numericMode, label);
  if (!meta.shortLabel || meta.shortLabel === "-") {
    return null;
  }

  return (
    <span
      className={`scr-participation-chip scr-participation-chip--${meta.tone}`}
      title={meta.fullLabel}
    >
      <i className={`fas ${meta.icon} scr-participation-chip__icon`} aria-hidden="true" />
      <span className="scr-participation-chip__text">{meta.shortLabel}</span>
    </span>
  );
};

const ModalDetailField = ({ label, value, highlight = false, variant, md = 6 }) => {
  const valueClass =
    variant === "success"
      ? "text-success"
      : variant === "danger"
        ? "text-danger"
        : highlight
          ? "text-primary"
          : "text-dark";

  return (
    <Col md={md} sm={6} xs={12} className="mb-0 pb-1">
      <div className="h-100 rounded px-2 py-1 scr-invoice-field-tile">
        <label
          className="text-muted d-block mb-0"
          style={{ fontSize: "0.86rem", letterSpacing: "0.04em" }}
        >
          {label.toUpperCase()}
        </label>
        <div className={`font-weight-bold text-break ${valueClass}`} style={{ fontSize: "1.05rem" }}>
          {value ?? "-"}
        </div>
      </div>
    </Col>
  );
};

const SkeletonBlock = ({ height = 14, width = "100%", className = "" }) => (
  <div
    className={`scr-invoice-detail-skeleton ${className}`.trim()}
    style={{ height, width }}
  />
);

const InvoiceDetailSkeleton = () => (
  <>
    <div className="scr-invoice-modal-split mb-1">
      <ModalPanel title="Shop Information" icon="fa-store" className="w-100">
        <Row className="mx-0 scr-invoice-shop-fields">
          {[1, 2, 3, 4].map((item) => (
            <Col key={item}>
              <SkeletonBlock height={48} />
            </Col>
          ))}
        </Row>
      </ModalPanel>
      <ModalPanel title="Invoice Information" icon="fa-file-invoice" className="w-100">
        <Row className="mx-0">
          <Col md={6} className="mb-2">
            <SkeletonBlock height={42} />
          </Col>
          <Col md={6} className="mb-2">
            <SkeletonBlock height={42} />
          </Col>
        </Row>
        <Row className="mx-0 mb-2">
          {[1, 2, 3].map((item) => (
            <Col xs={4} key={item} className="px-1 mb-2">
              <SkeletonBlock height={58} />
            </Col>
          ))}
        </Row>
        <SkeletonBlock height={6} className="mb-3" />
      </ModalPanel>
    </div>
    <div className="card mb-0 scr-invoice-transactions-panel" style={SCR_V2_NESTED_CARD_STYLE}>
      <div className="card-header py-2 bg-white d-flex align-items-center">
        <SkeletonBlock height={18} width="180px" />
      </div>
      <div className="card-body py-2 px-2">
        <SkeletonBlock height={72} className="mb-2" />
        <SkeletonBlock height={72} />
      </div>
    </div>
  </>
);

const ScopedAmountStrip = ({ scopedDueAmount, scopedReceivedAmount, varianceAmount }) => {
  const varianceClass =
    varianceAmount > 0 ? "text-danger" : varianceAmount < 0 ? "text-success" : "text-muted";

  return (
    <Row className="mx-0 mb-0 scr-invoice-amount-strip">
      {[
        { label: "Amount", value: formatCurrency(scopedDueAmount), className: "text-primary" },
        {
          label: "Received",
          value: formatCurrency(scopedReceivedAmount),
          className: "text-success",
        },
        {
          label: "Outstanding",
          value: formatCurrency(varianceAmount),
          className: varianceClass,
        },
      ].map((item) => (
        <Col xs={4} key={item.label} className="px-0">
          <div className="text-center border rounded py-1 px-1 bg-white h-100">
            <div className="text-muted text-uppercase mb-0" style={{ fontSize: "0.86rem" }}>
              {item.label}
            </div>
            <div className={`font-weight-bold ${item.className}`} style={{ fontSize: "1.05rem" }}>
              {item.value}
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

const getTransactionAccent = (transaction) => {
  if (transaction.isReversed) {
    return "#dc3545";
  }

  const category = (transaction.transactionCategory || "").toLowerCase();
  if (category.includes("nonvizlink") || category.includes("non-vizlink")) {
    return "#fd7e14";
  }
  if (category.includes("credit") || category.includes("recovery")) {
    return "#17a2b8";
  }
  if (category.includes("vizlink")) {
    return "#007bff";
  }

  return "#6c757d";
};

const getTransactionBadgeVariant = (transaction) => {
  if (transaction.isReversed) {
    return "danger";
  }

  const category = (transaction.transactionCategory || "").toLowerCase();
  if (category.includes("nonvizlink") || category.includes("non-vizlink")) {
    return "warning";
  }
  if (category.includes("credit") || category.includes("recovery")) {
    return "info";
  }
  if (category.includes("vizlink")) {
    return "vizlink";
  }

  return "secondary";
};

const getFieldValueClassName = (highlight) =>
  `${highlight ? "font-weight-bold text-primary" : "font-weight-bold"} text-break`;

const renderTransactionFieldValue = (field, onPreviewFile) => {
  if (field.isFile) {
    return (
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() =>
          onPreviewFile({
            url: field.value,
            title: field.label,
            isImage: field.isImage || isImagePath(field.value),
          })
        }
      >
        <i className="fas fa-eye mr-1" />
        View
      </Button>
    );
  }

  if (field.isComment) {
    return (
      <div className="scr-invoice-field-comment font-weight-bold text-break mb-0">
        {formatDynamicDisplayValue(field.value)}
      </div>
    );
  }

  if (field.isDateTime && field.value) {
    return (
      <div className={getFieldValueClassName(field.highlight)}>
        <Moment format="DD-MM-YYYY HH:mm">{field.value}</Moment>
      </div>
    );
  }

  if (field.isDate && field.value) {
    return (
      <div className={getFieldValueClassName(field.highlight)}>
        <Moment format="DD-MM-YYYY">{field.value}</Moment>
      </div>
    );
  }

  return (
    <div className={getFieldValueClassName(field.highlight)}>
      {field.highlight ? field.value : formatDynamicDisplayValue(field.value)}
    </div>
  );
};

const isVizLinkTransaction = (transaction) => {
  const category = (transaction.transactionCategory || "").toLowerCase();
  return (
    category.includes("vizlink") &&
    !category.includes("nonvizlink") &&
    !category.includes("non-vizlink")
  );
};

const TransactionDetailCard = ({ transaction, index, onPreviewFile }) => {
  // VizLink payments already show type/amount/category in the header — no expandable extras.
  const fieldRows = isVizLinkTransaction(transaction) ? [] : getTransactionFieldRows(transaction);
  const hasDetails = fieldRows.length > 0;
  const [expanded, setExpanded] = useState(false);
  const paymentLabel = transaction.paymentTypeMethod || transaction.transactionCategory || "Transaction";
  const accent = getTransactionAccent(transaction);
  const badgeVariant = getTransactionBadgeVariant(transaction);

  const toggleExpanded = () => {
    if (!hasDetails) {
      return;
    }
    setExpanded((open) => !open);
  };

  return (
    <div
      className={`card mb-0 w-100 scr-invoice-transaction-card${expanded ? " scr-invoice-transaction-card--expanded" : ""}`}
      style={{ ...SCR_V2_NESTED_CARD_STYLE, borderLeft: `4px solid ${accent}` }}
    >
      <div
        className={`card-header py-1 px-2 bg-white scr-invoice-transaction-card__header${hasDetails ? " scr-invoice-transaction-card__header--toggle" : ""}`}
        role={hasDetails ? "button" : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        aria-expanded={hasDetails ? expanded : undefined}
        onClick={hasDetails ? toggleExpanded : undefined}
        onKeyDown={
          hasDetails
            ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleExpanded();
              }
            }
            : undefined
        }
      >
        <div className="scr-invoice-transaction-card__main d-flex align-items-center min-w-0">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-muted mr-2 flex-shrink-0"
            style={{ width: 26, height: 26, fontSize: "0.75rem", fontWeight: 700 }}
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <div className="font-weight-bold text-break" style={{ fontSize: "1.05rem" }}>{paymentLabel}</div>
            {transaction.paymentDate ? (
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                <i className="far fa-calendar-alt mr-1" />
                <Moment format="DD-MM-YYYY HH:mm">{transaction.paymentDate}</Moment>
              </div>
            ) : null}
          </div>
        </div>

        <div className="scr-invoice-transaction-card__meta">
          <div className="scr-invoice-transaction-card__amount">
            <span className="font-weight-bold text-success">{formatCurrency(transaction.amount)}</span>
            {transaction.isReversed ? (
              <Badge bg="danger" className="ml-1">
                Reversed
              </Badge>
            ) : null}
          </div>
          <div className="scr-invoice-transaction-card__category">
            {transaction.transactionCategory ? (
              <Badge
                bg={badgeVariant === "vizlink" ? "secondary" : badgeVariant}
                className={badgeVariant === "vizlink" ? "scr-invoice-badge--vizlink" : undefined}
              >
                {transaction.transactionCategory}
              </Badge>
            ) : (
              <span className="scr-invoice-transaction-card__category-spacer" aria-hidden="true" />
            )}
          </div>
          <span
            className={`scr-invoice-transaction-card__chevron${expanded ? " is-expanded" : ""}${hasDetails ? "" : " is-hidden"}`}
            aria-hidden="true"
          >
            <i className="fas fa-chevron-down" />
          </span>
        </div>
      </div>
      {hasDetails && expanded ? (
        <div className="card-body py-2 px-3 bg-white">
          <Row className="mx-0">
            {fieldRows.map((field) => (
              <Col
                xs={12}
                md={field.fullWidth ? 12 : 6}
                lg={field.fullWidth ? 12 : 4}
                className="mb-2"
                key={`${transaction.id}-${field.label}`}
              >
                <div className="rounded px-2 py-2 scr-invoice-field-tile h-100">
                  <label
                    className="text-muted d-block mb-1"
                    style={{ fontSize: "0.86rem", letterSpacing: "0.04em" }}
                  >
                    {field.label.toUpperCase()}
                  </label>
                  {renderTransactionFieldValue(field, onPreviewFile)}
                </div>
              </Col>
            ))}
          </Row>
        </div>
      ) : null}
    </div>
  );
};

const InvoiceTransactionsPanel = ({ transactions = [], onPreviewFile }) => {
  const { paymentTransactions, adjustmentTransactions } = useMemo(() => {
    const payments = [];
    const adjustments = [];
    (transactions || []).forEach((tx) => {
      if (isAdjustmentTransaction(tx)) {
        adjustments.push(tx);
      } else {
        payments.push(tx);
      }
    });
    return { paymentTransactions: payments, adjustmentTransactions: adjustments };
  }, [transactions]);

  const hasPayments = paymentTransactions.length > 0;
  const hasAdjustments = adjustmentTransactions.length > 0;
  // Tabs only when both sides have data; otherwise show the single list only.
  const showTabs = hasPayments && hasAdjustments;
  const [activeTab, setActiveTab] = useState("transactions");

  useEffect(() => {
    if (showTabs) {
      setActiveTab("transactions");
      return;
    }
    setActiveTab(hasAdjustments ? "adjustments" : "transactions");
  }, [showTabs, hasPayments, hasAdjustments, transactions]);

  const activeList =
    (showTabs ? activeTab === "adjustments" : hasAdjustments && !hasPayments)
      ? adjustmentTransactions
      : paymentTransactions;

  const singleTitle = hasAdjustments && !hasPayments ? "Adjustments" : "Transactions";
  const singleCount = activeList.length;
  const singleIcon = hasAdjustments && !hasPayments ? "fa-sliders-h" : "fa-money-bill-wave";
  const singleIconClass =
    hasAdjustments && !hasPayments ? "bg-secondary" : "bg-success";

  return (
    <div className="card mb-0 scr-invoice-transactions-panel" style={SCR_V2_NESTED_CARD_STYLE}>
      <div className="card-header bg-white scr-invoice-transactions-panel__header">
        {showTabs ? (
          <ul className="nav nav-tabs border-0 mb-0 scr-invoice-tabs scr-invoice-transactions-panel__tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link py-1 px-2 ${
                  activeTab === "transactions" ? "active" : ""
                }`}
                onClick={() => setActiveTab("transactions")}
              >
                Transactions
                <span className="badge badge-light border text-dark ml-1">
                  {paymentTransactions.length}
                </span>
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link py-1 px-2 ${
                  activeTab === "adjustments" ? "active" : ""
                }`}
                onClick={() => setActiveTab("adjustments")}
              >
                Adjustments
                <span className="badge badge-light border text-dark ml-1">
                  {adjustmentTransactions.length}
                </span>
              </button>
            </li>
          </ul>
        ) : (
          <div className="d-flex align-items-center min-w-0">
            <span
              className={`d-inline-flex align-items-center justify-content-center rounded text-white mr-2 flex-shrink-0 scr-invoice-transactions-panel__icon ${singleIconClass}`}
              style={{ width: 24, height: 24, fontSize: "0.75rem" }}
            >
              <i className={`fas ${singleIcon}`} />
            </span>
            <h6 className="font-weight-bold mb-0" style={{ fontSize: "1.05rem" }}>{singleTitle}</h6>
            {singleCount > 0 ? (
              <Badge bg="secondary" className="ml-2">
                {singleCount}
              </Badge>
            ) : null}
          </div>
        )}
      </div>
      <div className="card-body px-1 py-1">
        {activeList.length === 0 ? (
          <div className="text-muted text-center py-3" style={{ fontSize: "0.9rem" }}>
            <i className="fas fa-inbox fa-2x mb-2 d-block opacity-50" />
            No transaction details available.
          </div>
        ) : (
          activeList.map((transaction, index) => (
            <TransactionDetailCard
              key={`${transaction.transactionCategory}-${transaction.id}`}
              transaction={transaction}
              index={index}
              onPreviewFile={onPreviewFile}
            />
          ))
        )}
      </div>
    </div>
  );
};

const SCRV2InvoiceDetailModal = ({
  show,
  rowContext,
  detail,
  loading,
  error,
  report,
  onClose,
  onPreviewFile,
}) => {
  const printRef = useRef(null);

  if (!show) {
    return null;
  }

  const invoiceNo =
    detail?.vizproInvoiceNumber ||
    detail?.invoiceNumber ||
    rowContext?.invoiceNo ||
    "-";
  const totals = detail?.totals || {};
  const transactions = detail?.transactions || [];
  const canPrint = Boolean(detail) && !loading;

  return (
    <>
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className="scr-invoice-detail-modal"
      dialogClassName="scr-invoice-detail-modal__dialog modal-lg"
    >
      <Modal.Header closeButton className="py-2 bg-white border-bottom">
        <div className="d-flex align-items-center w-100 pr-3">
          <span className="scr-invoice-detail-modal__header-icon mr-3 flex-shrink-0">
            <i className="fas fa-file-invoice" />
          </span>
          <div className="min-w-0">
            <Modal.Title className="h5 mb-0 font-weight-bold">
              SCR Invoice Detail
            </Modal.Title>
            <div className="text-muted text-truncate" style={{ fontSize: "0.88rem" }}>
              {invoiceNo}
              {report.scrNo ? ` · ${report.scrNo}` : ""}
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="px-3 pt-2 pb-3 bg-light" style={MODAL_BODY_SCROLL_STYLE}>
        {loading && !detail ? <InvoiceDetailSkeleton /> : null}

        {!loading && error ? (
          <div className="alert alert-danger mb-0" role="alert">
            <i className="fas fa-exclamation-triangle mr-2" />
            {error}
          </div>
        ) : null}

        {detail ? (
          <>
            <div className="scr-invoice-modal-split mb-1">
              <ModalPanel title="Shop Information" icon="fa-store" className="w-100">
                <Row className="mx-0 scr-invoice-shop-fields">
                  <ModalDetailField label="Shop Name" value={rowContext?.shopName} />
                  <ModalDetailField label="DLM QR Code" value={report.deliveryQRCode} />
                  <ModalDetailField label="Shop Code" value={rowContext?.vizShopCode} />
                  <ModalDetailField label="DLM Name" value={report.dlmName || report.dlmUserName} />
                </Row>
              </ModalPanel>
              <ModalPanel
                title="Invoice Information"
                icon="fa-file-invoice"
                className="w-100"
                headerExtra={
                  <ParticipationModeChip
                    mode={detail.participationMode}
                    label={detail.participationModeLabel}
                  />
                }
              >
                <div className="scr-invoice-invoice-stack">
                  <Row className="mx-0 scr-invoice-invoice-fields">
                    <ModalDetailField label="Invoice No" value={invoiceNo} />
                    <ModalDetailField
                      label="Invoice Date"
                      value={
                        detail.invoiceDate ? (
                          <Moment format="DD-MM-YYYY">{detail.invoiceDate}</Moment>
                        ) : (
                          "-"
                        )
                      }
                    />
                    {detail.dueDate ? (
                      <ModalDetailField
                        label="Due Date"
                        value={<Moment format="DD-MM-YYYY">{detail.dueDate}</Moment>}
                      />
                    ) : null}
                    {Number(totals.totalCreditAmount || 0) !== 0 ? (
                      <ModalDetailField
                        label="Credit"
                        value={formatCurrency(totals.totalCreditAmount)}
                      />
                    ) : null}
                    {Number(totals.totalRecoveryAmount || 0) !== 0 ? (
                      <ModalDetailField
                        label="Recovery"
                        value={formatCurrency(totals.totalRecoveryAmount)}
                      />
                    ) : null}
                    {Number(totals.totalAdjustmentAmount || 0) !== 0 ? (
                      <ModalDetailField
                        label="Adjustments"
                        value={formatCurrency(totals.totalAdjustmentAmount)}
                      />
                    ) : null}
                    {Number(totals.totalReversalAmount || 0) !== 0 ? (
                      <ModalDetailField
                        label="Reversals"
                        value={formatCurrency(totals.totalReversalAmount)}
                      />
                    ) : null}
                  </Row>
                  <ScopedAmountStrip
                    scopedDueAmount={Number(totals.scopedDueAmount || 0)}
                    scopedReceivedAmount={Number(totals.scopedReceivedAmount || 0)}
                    varianceAmount={Number(totals.varianceAmount || 0)}
                  />
                </div>
              </ModalPanel>
            </div>

            <InvoiceTransactionsPanel
              transactions={transactions}
              onPreviewFile={onPreviewFile}
            />
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="py-2 bg-white border-top d-flex justify-content-between align-items-center">
        <ReactToPrint
          trigger={() => (
            <Button variant="outline-secondary" disabled={!canPrint}>
              <i className="fa fa-print mr-1" />
              Print
            </Button>
          )}
          content={() => printRef.current}
          pageStyle={`
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            @media print {
              html, body {
                width: 100% !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .scr-v2-invoice-print-view {
                width: 100% !important;
                max-width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          `}
        />
        <Button variant="outline-secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    {detail ? (
      <div style={{ display: "none" }} aria-hidden="true">
        <SCRV2InvoiceDetailPrint
          ref={printRef}
          detail={detail}
          report={report}
          rowContext={rowContext}
        />
      </div>
    ) : null}
    </>
  );
};

export default SCRV2InvoiceDetailModal;
